import { type GeneratedNodeAttribute, isFileAttributeType, isTableAttributeType } from "@keydials/api-client";
import { richTextHasContent } from "@keydials/ui";
import { partitionFieldsets } from "./attributeFieldsets";
import { type AttributeFilesContext, type AttributeNodeScope, fileAttributeState } from "./fileAttribute";
import { gateValueFilled } from "./statusGate";
import { tableAttributeState } from "./tableAttribute";

/**
 * The action point's Overview as a to-do list (KEY-569): what this node asks for, and which of it has an
 * answer — built from its fields rather than authored separately, so it cannot drift from them.
 *
 * Two rules carry the design:
 * - **It lists exactly what the Info tab draws.** The caller passes the declarations `nodeAttributeGroups`
 *   resolved for Info (visibility, scope, and the status gate's hidden-while-empty rule), and nothing here
 *   filters them again. A second filter is how a list ends up naming a field Info hides.
 * - **"Added" means what a person would see on Info.** The status gate's rule — any non-blank stored value —
 *   is the starting point, but an empty rich-text body, a table with no rows, or a file field whose files
 *   have gone would each say "Added" beside a field showing nothing. The gate itself keeps the server's rule.
 */

/** Whether a field has an answer someone could see. */
export type AttributeAnswer = "filled" | "empty" | "unavailable";

export interface AnswerContext {
  /** The action point's loaded files. */
  files?: AttributeFilesContext;
  /** The node the fields hang off, for picking a file field's own files out of `files`. */
  scope: AttributeNodeScope;
  /**
   * Whether `files` can be trusted to hold every file this node's fields name. False until the section's
   * list has loaded, in the admin preview, and for a reader the dial doesn't let see files (KEY-537) — in each
   * of which an empty list means "not known", not "gone", so a file field falls back to the gate's rule.
   */
  filesReliable: boolean;
}

export function attributeAnswer(decl: GeneratedNodeAttribute, value: string, context: AnswerContext): AttributeAnswer {
  if (!gateValueFilled(value)) {
    return "empty";
  }
  if (decl.type === "RichText") {
    return richTextHasContent(value) ? "filled" : "empty";
  }
  if (isTableAttributeType(decl.type)) {
    // A value that no longer fits the columns still holds data — Info flags it rather than dropping it.
    const table = tableAttributeState(decl, value);
    return table.malformed || table.rows.length > 0 ? "filled" : "empty";
  }
  if (isFileAttributeType(decl.type)) {
    if (!context.filesReliable) {
      return "filled";
    }
    const state = fileAttributeState(decl, value, context.scope, false, context.files);
    if (state.link || state.attached.length > 0) {
      return "filled";
    }
    // The value names files, but none of them resolve: quarantined, or deleted by an admin.
    return state.documentIds.length > 0 ? "unavailable" : "empty";
  }
  return "filled";
}

export interface ChecklistRow {
  /** `Segment:key` or `Dial:key` — a Segment field and a dial-wide one may share a key. */
  id: string;
  decl: GeneratedNodeAttribute;
  answer: AttributeAnswer;
  /** Empty, though Info shows a suggested answer in the box that nobody has saved (KEY-286). */
  suggested: boolean;
  /** This viewer can fill it in, so the row may take them to it. */
  editable: boolean;
  /** One of the fields this action point's items are held back for (KEY-460). */
  neededFirst: boolean;
  /** A parked status pick is waiting on it (KEY-248). */
  demanded: boolean;
}

export interface ChecklistGroup {
  /** An authored group heading (KEY-436), {@link SHARED_FIELDS_TITLE} for placed dial-wide fields, or null. */
  title: string | null;
  rows: ChecklistRow[];
}

export interface OverviewChecklistModel {
  groups: ChecklistGroup[];
  total: number;
  filled: number;
  /** Whether any row can take this viewer to its field. */
  anyEditable: boolean;
}

/** The heading dial-wide fields placed on a node sit under, on Info and on Overview alike (KEY-378). */
export const SHARED_FIELDS_TITLE = "Shared across this dial";

export interface ChecklistInput extends AnswerContext {
  /** The action point's own fields, exactly as Info draws them. */
  segmentDecls: readonly GeneratedNodeAttribute[];
  /** Dial-wide fields placed on this action point (KEY-378), exactly as Info draws them. */
  placedDecls: readonly GeneratedNodeAttribute[];
  segmentValueOf: (attrKey: string) => string;
  dialValueOf: (attrKey: string) => string;
  canEditAttributes: boolean;
  access?: (decl: GeneratedNodeAttribute) => { see: boolean; edit: boolean };
  /** Keys of the fields the server holds this action point's items back for (KEY-460). */
  neededFirstKeys: ReadonlySet<string>;
  /** Keys a parked status pick is waiting on. */
  demandedKeys: ReadonlySet<string>;
}

export function buildOverviewChecklist(input: ChecklistInput): OverviewChecklistModel {
  const rowFor = (decl: GeneratedNodeAttribute, target: "Segment" | "Dial"): ChecklistRow => {
    const value = target === "Segment" ? input.segmentValueOf(decl.key) : input.dialValueOf(decl.key);
    const answer = attributeAnswer(decl, value, input);
    // The test Info applies before it offers an editable field: attribute rights, the field's own rule
    // (KEY-299), never a field answered on a child dial (KEY-483) — and for a file field a live backend and
    // upload rights too, since attaching a file is a higher bar than writing a value.
    let editable = input.canEditAttributes && !decl.sourceChildDefinitionId && (input.access?.(decl).edit ?? true);
    if (editable && isFileAttributeType(decl.type)) {
      editable = fileAttributeState(decl, value, input.scope, true, input.files).editable;
    }
    return {
      id: `${target}:${decl.key}`,
      decl,
      answer,
      suggested: answer === "empty" && !!decl.defaultValue?.trim(),
      editable,
      neededFirst: target === "Segment" && input.neededFirstKeys.has(decl.key),
      demanded: target === "Segment" && input.demandedKeys.has(decl.key),
    };
  };

  const groups: ChecklistGroup[] = [];
  const loose: ChecklistRow[] = [];
  // Info's order: authored groups first, then the fields standing on their own (KEY-558).
  for (const run of partitionFieldsets(input.segmentDecls)) {
    if (run.kind === "fieldset") {
      groups.push({ title: run.title, rows: run.decls.map((d) => rowFor(d, "Segment")) });
    } else {
      loose.push(rowFor(run.decl, "Segment"));
    }
  }
  if (loose.length > 0) {
    groups.push({ title: null, rows: loose });
  }
  if (input.placedDecls.length > 0) {
    groups.push({ title: SHARED_FIELDS_TITLE, rows: input.placedDecls.map((d) => rowFor(d, "Dial")) });
  }

  const rows = groups.flatMap((g) => g.rows);
  return {
    groups,
    total: rows.length,
    filled: rows.filter((r) => r.answer === "filled").length,
    anyEditable: rows.some((r) => r.editable),
  };
}

/**
 * How the action point's own status frames the list.
 * - `todo` — the ordinary case, and every item-scored action point.
 * - `record` — a finished status: what was given, without asking for the rest (as a Complete item isn't nagged).
 * - `excluded` — a status left out of the dial's count, such as Not required: nothing here is owed.
 */
export type ChecklistFrame = "todo" | "record" | "excluded";

/** The line above the list, or null when there is no list to introduce. */
export function checklistHeadline(model: OverviewChecklistModel, frame: ChecklistFrame): string | null {
  const { total, filled } = model;
  if (frame === "excluded" || total === 0) {
    return null;
  }
  if (frame === "record") {
    return `${filled} of ${total} ${total === 1 ? "detail" : "details"} recorded.`;
  }
  if (filled === 0) {
    return `Nothing added yet — ${total} ${total === 1 ? "thing" : "things"} to fill in.`;
  }
  if (filled === total) {
    return "Everything here is filled in.";
  }
  return `${filled} of ${total} filled in.`;
}

/** What a row says about its field's answer. */
export function rowStateText(row: ChecklistRow, frame: ChecklistFrame): string {
  if (row.answer === "filled") {
    return "Added";
  }
  if (row.answer === "unavailable") {
    return "The file here is no longer available";
  }
  if (frame === "record") {
    return "Left blank";
  }
  return row.suggested ? "Suggested answer not saved yet" : "Not added yet";
}
